import type { FormEvent, ReactNode } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { Icon } from "@/components/common/Icon";

interface EnvironmentSettingsFormProps {
	testId: string;
	onSubmit: (e: FormEvent) => void;
	loading: boolean;
	saved: boolean;
	submitError: string | null;
	/** 保存できる状態でなければ true (保存する元の一覧を読めていないときなど)。 */
	submitDisabled?: boolean;
	children: ReactNode;
}

/** 起動時設定のタブに共通する枠。項目をスクロール領域に並べ、保存結果と保存ボタンを下に固定する。 */
export function EnvironmentSettingsForm({
	testId,
	onSubmit,
	loading,
	saved,
	submitError,
	submitDisabled = false,
	children,
}: EnvironmentSettingsFormProps) {
	return (
		<form
			onSubmit={onSubmit}
			noValidate
			data-testid={testId}
			style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
		>
			<div
				style={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					padding: "20px 24px",
					display: "flex",
					flexDirection: "column",
					gap: 16,
				}}
			>
				{children}
				{submitError && (
					<div
						style={{
							padding: "10px 14px",
							borderRadius: "var(--radius-3)",
							background: "var(--danger-subtle)",
							boxShadow: "0 0 0 1px var(--danger-line)",
							fontSize: "var(--text-ui)",
							color: "var(--danger)",
							fontFamily: "var(--font-sans)",
						}}
					>
						{submitError}
					</div>
				)}
				<div
					style={{
						display: "flex",
						alignItems: "flex-start",
						gap: 6,
						paddingTop: 4,
						fontSize: "var(--text-caption)",
						lineHeight: "var(--leading-normal)",
						color: "var(--fg-2)",
						fontFamily: "var(--font-sans)",
					}}
				>
					<span
						style={{
							flex: "none",
							display: "inline-flex",
							alignItems: "center",
							height: "1.5em",
						}}
					>
						<Icon name="info" size={13} />
					</span>
					<span>設定の変更は、このエージェントの次回起動時から反映されます。</span>
				</div>
			</div>
			<div
				style={{
					flex: "none",
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					gap: 12,
					borderTop: "var(--pane-header-border)",
					padding: "12px 24px",
				}}
			>
				{saved && (
					<span
						data-testid="environment-settings-saved"
						style={{
							fontSize: "var(--text-caption)",
							color: "var(--fg-2)",
							fontFamily: "var(--font-sans)",
						}}
					>
						保存しました。
					</span>
				)}
				<ConfirmButton type="submit" label="保存" loading={loading} disabled={submitDisabled} />
			</div>
		</form>
	);
}
